import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
}

const BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none border ' +
  'transition-[background-color,color,transform] duration-fast ease-linear active:scale-[0.98] ' +
  'focus-visible:outline-none focus-visible:shadow-ring disabled:opacity-35 disabled:pointer-events-none';

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-control-sm px-[14px]',
  md: 'h-control-md px-[18px]',
  lg: 'h-control-lg px-[18px]',
};

/* Primary = PAPER: the money action is simply the brightest thing on screen.
 * Ghost = underlined mono ledger-speak ("OPEN A DISPUTE."). */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-paper text-ink border-transparent hover:bg-paper-bright active:bg-paper-press',
  secondary: 'bg-transparent text-fg-1 border-strong hover:bg-overlay-hover active:bg-overlay-press',
  ghost:
    'bg-transparent text-fg-2 border-transparent font-mono text-[12px] uppercase tracking-[0.12em] ' +
    'underline underline-offset-[3px] hover:text-fg-1',
  danger: 'bg-transparent text-dispute border-dispute hover:bg-dispute-soft active:bg-dispute-soft',
};

const LABEL: Record<ButtonSize, string> = {
  sm: 'font-body font-bold text-[13px] tracking-[0.04em]',
  md: 'font-body font-bold text-[15px] tracking-[0.04em]',
  lg: 'font-body font-bold text-[15px] tracking-[0.04em]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  icon,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        BASE,
        SIZES[size],
        variant === 'ghost' ? '' : LABEL[size],
        VARIANTS[variant],
        block && 'flex w-full',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
